import { useCallback, useMemo, useState } from "react";
import { defineMessages } from "react-intl";
import classNames from "classnames";
import times from "lodash/times";
import sumBy from "lodash/sumBy";

import inputConnect from "./inputConnect";
import { useFormatMessage } from "../Intl/Context";

const BudgetDispatcherInput = props => {
  const { values, budget, options, maxBudgetPerOption, updateValue } = props;
  const [hoveredOption, setHoveredOption] = useState({});
  const { optionId: hoveredOptionId, value: hoveredOptionValue } = hoveredOption;

  const spentBudget = sumBy(values, ({ value }) => value);
  const remainingBudget = budget - spentBudget;

  const t = useFormatMessage();

  const remainingBudgetLabel = t(intlMessages.remainingBudget, {
    count: remainingBudget,
    b: count =>
      `<b class="bg-primary d-inline-block padding-x-xxs border-radius">${count}</b>`
  });

  const valuesByOptionId = useMemo(
    () =>
      // Loop though the options and set the value to 0 if it's not in the values
      // to have a default value for each option set to 0.
      options.reduce((obj, option) => {
        const value = values.find(({ optionId }) => optionId === option.id);
        return {
          ...obj,
          [option.id]: value ? value.value : 0
        };
      }, {}),
    [options, values]
  );

  const valueFor = useCallback(
    identifier => valuesByOptionId[identifier] || 0,
    [valuesByOptionId]
  );

  const isValidTargetValueFor = useCallback(
    (option, targetValue = hoveredOptionValue) => {
      const optionCurrentValue = valueFor(option.id);
      const otherOptionsBudgetSpent = spentBudget - optionCurrentValue;
      const targetBudget = otherOptionsBudgetSpent + targetValue;

      return targetBudget <= budget;
    },
    [budget, hoveredOptionValue, spentBudget, valueFor]
  );

  const handleStarClick = useCallback(
    (option, value) => {
      // If the current star value is clicked again, we remove the value and set
      // it to 0.
      const currentValue = valueFor(option.id);
      value = currentValue === value ? 0 : value;

      if (!isValidTargetValueFor(option, value)) return;

      const valuesHash = { ...valuesByOptionId, [option.id]: value };

      const values = Object.keys(valuesHash).map(key => ({
        optionId: key,
        value: valuesHash[key]
      }));

      updateValue({ values });
    },
    [isValidTargetValueFor, updateValue, valueFor, valuesByOptionId]
  );

  const handleStarMouseEnter = useCallback(
    (option, value) => {
      // If a new option is hovered, we set the hoveredOption state to this star
      if (hoveredOptionId !== option.id || hoveredOptionValue !== value) {
        setHoveredOption({ optionId: option.id, value });
      }
    },
    [hoveredOptionId, hoveredOptionValue]
  );

  const handleStarMouseLeave = useCallback(
    (option, value) => {
      // If the current hovered option is left, we reset the current hoveredOption state
      if (hoveredOptionId === option.id && hoveredOptionValue === value) {
        setHoveredOption({});
      }
    },
    [hoveredOptionId, hoveredOptionValue]
  );

  const starFor = useCallback(
    (option, index) => {
      const value = index + 1;

      const optionIsHovered = hoveredOptionId === option.id;
      const optionCurrentValue = valueFor(option.id);
      const optionIsCurrentvalue = optionCurrentValue >= value;

      const isHovered = optionIsHovered && hoveredOptionValue >= value;
      const isActive = optionIsCurrentvalue && !optionIsHovered;
      const isEmpty = !isHovered && (!isActive || optionIsHovered);
      const isInvalid = !isEmpty && optionIsHovered && !isValidTargetValueFor(option);

      const countValue = value === optionCurrentValue && optionIsCurrentvalue ? 0 : value;

      const starLabel = t(intlMessages.starLabel, {
        count: countValue
      });

      const className = classNames([
        "star",
        {
          "text-white": isEmpty,
          "star-empty": isEmpty,
          "text-primary-80": !isInvalid && isHovered,
          "text-border-primary-80": !isInvalid && isHovered,
          "text-primary": isActive,
          "text-border-primary": isActive,
          "star-invalid": isInvalid
        }
      ]);

      const isDisabled = remainingBudget === 0 && isEmpty === true;

      return (
        <button
          aria-label={starLabel}
          role="radio"
          title={starLabel}
          aria-checked={optionIsCurrentvalue ? "true" : "false"}
          key={index}
          type="button"
          className={className}
          onClick={() => handleStarClick(option, value)}
          onMouseEnter={() => handleStarMouseEnter(option, value)}
          onMouseLeave={() => handleStarMouseLeave(option, value)}
          disabled={isDisabled}
        />
      );
    },
    [
      handleStarClick,
      handleStarMouseEnter,
      handleStarMouseLeave,
      hoveredOptionId,
      hoveredOptionValue,
      isValidTargetValueFor,
      remainingBudget,
      t,
      valueFor
    ]
  );

  return (
    <div className="budget-dispatcher-input">
      <ul className="padding-none">
        {options.map(option => (
          <li key={option.id} className="budget-dispatcher-input-option margin-top-xs">
            <div id={option.id} className="flex-1 text-small d-flex align-items-center">
              {option.name}
            </div>

            <div className="stars-container">
              <div
                role="radiogroup"
                aria-labelledby={option.id}
                className="stars-input text-greater"
              >
                {times(maxBudgetPerOption, index => starFor(option, index))}
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div
        className="budget-dispatcher-input-budget margin-top-xs text-small text-right text-muted"
        dangerouslySetInnerHTML={{ __html: remainingBudgetLabel }}
      />
    </div>
  );
};

export default inputConnect(BudgetDispatcherInput, {
  fields: { values: [] }
});

const intlMessages = defineMessages({
  remainingBudget: {
    id: "inputs.budgetDispatcher.remainingBudget",
    defaultMessage: "Il vous reste <b>{count}</b> étoiles à répartir"
  },
  starLabel: {
    id: "inputs.budgetDispatcher.starLabel",
    defaultMessage: `{count, plural,
      =0 { Remettre à zéro les étoiles pour cette option }
      one { Choisir { count } étoile pour cette option }
      other { Choisir { count } étoiles pour cette option }
    }`
  }
});
